import React, { useCallback, useEffect, useRef, useState } from 'react';

import type {
	InvitedAppShortcutAllowedSourceAppKey,
	InvitedAppShortcutAllowedTargetAppKey,
} from '../../../../../common/types';
import {
	type CollaboratorProduct,
	getCollaboratorsInProducts,
} from '../../../../../services/collaborators-in-products';
import { useInvitedAppAnalytics } from '../../../../../services/invited-app-shortcuts/invited-app-analytics';
import {
	PrefetchContext,
	StateContext,
	type CollaboratorsByApp,
	type InvitedAppCollaboratorsLazyState,
	type PrefetchAll,
} from '../../../../../services/invited-app-shortcuts/invited-app-shortcuts-collaborators';

/**
 * Narrows a BFF `CollaboratorProduct` to the matching invited-shortcuts target.
 */
const isAllowedTargetApp = (
	product: CollaboratorProduct,
	allowed: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>,
): product is InvitedAppShortcutAllowedTargetAppKey & CollaboratorProduct =>
	(allowed as ReadonlyArray<CollaboratorProduct>).includes(product);

export interface InvitedAppCollaboratorsLazyProviderProps {
	children: React.ReactNode;
	/** Cloud ID of the current tenant. */
	cloudId: string;
	/**
	 * Apps eligible to show a shortcut. The provider fires a single
	 * combined `getCollaboratorsInProducts` request for them on first
	 * `prefetchAll()` call. The latest reference is read at fire-time via
	 * an internal `depsRef`, so passing an unmemoised array is safe — it
	 * does not re-issue an in-flight or completed fetch.
	 */
	eligibleApps: ReadonlyArray<InvitedAppShortcutAllowedTargetAppKey>;
	/** Source product, forwarded to the existing collaborators analytics. */
	sourceApp: InvitedAppShortcutAllowedSourceAppKey;
	/** Account ID of the current user. */
	userId: string;
}

/**
 * Owns a singleton `getCollaboratorsInProducts` request for all eligible
 * apps. `prefetchAll()` is idempotent per provider instance; cancellation
 * is the caller's responsibility. Read state via
 * `useInvitedAppCollaboratorsLazy()`, trigger via
 * `useInvitedAppCollaboratorsLazyPrefetch()` — split so hover handlers
 * stay stable. Empty `eligibleApps` short-circuits to `ready` with no
 * HTTP request.
 */
export const InvitedAppCollaboratorsLazyProvider = ({
	cloudId,
	userId,
	eligibleApps,
	sourceApp,
	children,
}: InvitedAppCollaboratorsLazyProviderProps): JSX.Element => {
	const analytics = useInvitedAppAnalytics();

	const [state, setState] = useState<InvitedAppCollaboratorsLazyState>(() =>
		eligibleApps.length === 0 ? { status: 'ready', collaboratorsByApp: {} } : { status: 'idle' },
	);

	// Singleton guard. Cleared on `.catch` for retry; kept after `.then`
	// so a successful fetch is never re-issued.
	const inFlightRef = useRef<Promise<unknown> | null>(null);

	// Prevents state updates after unmount.
	const isMountedRef = useRef(true);
	useEffect(
		() => () => {
			isMountedRef.current = false;
		},
		[],
	);

	// Latest-props ref keeps `prefetchAll` identity-stable so an unmemoised
	// `eligibleApps` array doesn't invalidate hover handlers downstream.
	const depsRef = useRef({ analytics, cloudId, eligibleApps, sourceApp, userId });
	depsRef.current = { analytics, cloudId, eligibleApps, sourceApp, userId };

	const prefetchAll = useCallback<PrefetchAll>(() => {
		if (inFlightRef.current !== null) {
			return;
		}

		const { analytics, cloudId, eligibleApps, sourceApp, userId } = depsRef.current;

		if (eligibleApps.length === 0) {
			// Nothing to fetch; provider is already in the `ready` empty state.
			return;
		}

		setState({ status: 'loading' });

		// Cross-validated against `InvitedAppShortcutAllowedTargetAppKey` inside
		// the response loop via `isAllowedTargetApp`. Any future BFF-only
		// product will be filtered out, never indexed into `collaboratorsByApp`.
		const products: ReadonlyArray<CollaboratorProduct> = eligibleApps;

		// Guard against a synchronous throw before the Promise is created —
		// otherwise `setState('loading')` would leave the provider stuck.
		let pending: ReturnType<typeof getCollaboratorsInProducts>;
		try {
			pending = getCollaboratorsInProducts({ cloudId, userId, products });
		} catch (error) {
			inFlightRef.current = null;
			if (!isMountedRef.current) {
				return;
			}
			setState({ status: 'error', error });
			analytics.fireFetchCollaboratorsAccessFailed({
				sourceApp,
				targetApps: eligibleApps,
				error: error instanceof Error ? error.message : 'unknown',
			});
			return;
		}

		inFlightRef.current = pending
			.then((rows) => {
				if (!isMountedRef.current) {
					return;
				}
				const collaboratorsByApp: CollaboratorsByApp = {};
				for (const { collaborator, products: collaboratorProducts } of rows) {
					for (const product of collaboratorProducts) {
						if (!isAllowedTargetApp(product, eligibleApps)) {
							continue;
						}
						// `product` is narrowed to `InvitedAppShortcutAllowedTargetAppKey`.
						const slice = collaboratorsByApp[product] ?? [];
						slice.push({
							key: collaborator.id,
							name: collaborator.name,
							src: collaborator.avatar,
						});
						collaboratorsByApp[product] = slice;
					}
				}
				setState({ status: 'ready', collaboratorsByApp });
				analytics.fireFetchCollaboratorsAccessSucceeded({
					sourceApp,
					targetApps: eligibleApps,
					collaborators: rows.length,
				});
			})
			.catch((error: unknown) => {
				// Allow caller-driven retry on the next hover when the fetch
				// failed (network blip, 5xx, etc.). Successful fetches keep
				// the ref set so they are not re-issued.
				inFlightRef.current = null;
				if (!isMountedRef.current) {
					return;
				}
				setState({ status: 'error', error });
				analytics.fireFetchCollaboratorsAccessFailed({
					sourceApp,
					targetApps: eligibleApps,
					// Normalize to avoid leaking raw server error detail into analytics.
					error: error instanceof Error ? error.message : 'unknown',
				});
			});
	}, []);

	return (
		<PrefetchContext.Provider value={prefetchAll}>
			<StateContext.Provider value={state}>{children}</StateContext.Provider>
		</PrefetchContext.Provider>
	);
};
